import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  ArrowLeft, 
  Users, 
  Mail, 
  Download, 
  RefreshCw,
  Calendar,
  Briefcase,
  Lock
} from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [activeTab, setActiveTab] = useState("waitlist");
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [partnerApplications, setPartnerApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if already authenticated
  useEffect(() => {
    const authStatus = sessionStorage.getItem("evolve_admin_auth");
    if (authStatus === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    try {
      const response = await axios.get(`${API}/admin/verify?password=${encodeURIComponent(password)}`);
      if (response.data.valid) {
        setIsAuthenticated(true);
        sessionStorage.setItem("evolve_admin_auth", "true");
        setPasswordError(false);
      } else {
        setPasswordError(true);
      }
    } catch (err) {
      setPasswordError(true);
    } finally {
      setIsVerifying(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [waitlistRes, partnersRes] = await Promise.all([
        axios.get(`${API}/waitlist/all`),
        axios.get(`${API}/partners/all`)
      ]);
      setWaitlistEntries(waitlistRes.data.entries || []);
      setPartnerApplications(partnersRes.data.applications || []);
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  // Password Screen
  if (!isAuthenticated) {
    return (
      <main className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="admin-login">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-[#00FFD1]/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#00FFD1]" />
            </div>
            <h1 className="text-2xl font-black text-white">Admin Access</h1>
            <p className="text-gray-400 mt-2">Enter password to continue</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError(false);
              }}
              placeholder="Enter password"
              className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder:text-gray-500 focus:ring-1 outline-none transition-all ${
                passwordError 
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500" 
                  : "border-white/10 focus:border-[#00FFD1] focus:ring-[#00FFD1]"
              }`}
              data-testid="admin-password-input"
              autoFocus
            />
            {passwordError && (
              <p className="text-red-400 text-sm">Incorrect password</p>
            )}
            <button
              type="submit"
              disabled={isVerifying}
              className="w-full py-3 bg-[#00FFD1] text-black font-bold rounded-xl hover:bg-[#00FFD1]/90 transition-all disabled:opacity-70"
              data-testid="admin-login-btn"
            >
              {isVerifying ? "Verifying..." : "Access Dashboard"}
            </button>
          </form>

          <Link 
            to="/" 
            className="block text-center mt-6 text-gray-400 hover:text-[#00FFD1] transition-colors text-sm"
          >
            ← Back to Home
          </Link>
        </motion.div>
      </main>
    );
  }

  const exportToCSV = (data, filename) => {
    if (data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] || "";
          // Escape commas and quotes
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(",")
      )
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <main className="min-h-screen bg-black py-12 px-6" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-400 hover:text-[#00FFD1] transition-colors mb-4"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
              Admin Dashboard
            </h1>
          </div>
          <button
            onClick={fetchData}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white hover:bg-white/20 transition-colors disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 rounded-2xl bg-[#0A0A0A] border border-white/10"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#00FFD1]/10 flex items-center justify-center">
                <Mail className="w-6 h-6 text-[#00FFD1]" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Waitlist Signups</p>
                <p className="text-3xl font-bold text-white">{waitlistEntries.length}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-6 rounded-2xl bg-[#0A0A0A] border border-white/10"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#00FFD1]/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-[#00FFD1]" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Partner Applications</p>
                <p className="text-3xl font-bold text-white">{partnerApplications.length}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("waitlist")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "waitlist"
                ? "bg-[#00FFD1] text-black"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
            data-testid="waitlist-tab"
          >
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Waitlist ({waitlistEntries.length})
            </span>
          </button>
          <button
            onClick={() => setActiveTab("partners")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "partners"
                ? "bg-[#00FFD1] text-black"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
            data-testid="partners-tab"
          >
            <span className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Partners ({partnerApplications.length})
            </span>
          </button>
        </div>

        {/* Error State */}
        {error && (
          <div className="p-4 mb-6 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
            {error}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-[#00FFD1] animate-spin" />
          </div>
        )}

        {/* Waitlist Table */}
        {!isLoading && activeTab === "waitlist" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-2xl bg-[#0A0A0A] border border-white/10 overflow-hidden"
          >
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Waitlist Entries</h2>
              <button
                onClick={() => exportToCSV(waitlistEntries, "evolve_waitlist")}
                disabled={waitlistEntries.length === 0}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#00FFD1]/10 text-[#00FFD1] rounded-lg hover:bg-[#00FFD1]/20 transition-colors disabled:opacity-50 text-sm"
                data-testid="export-waitlist-btn"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>

            {waitlistEntries.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                No waitlist entries yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">#</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlistEntries.map((entry, index) => (
                      <tr 
                        key={entry.id || index} 
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                        <td className="px-4 py-3 text-sm text-white font-medium">{entry.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          <span className="flex items-center gap-2">
                            <Calendar className="w-3 h-3" />
                            {formatDate(entry.created_at)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        {/* Partners Table */}
        {!isLoading && activeTab === "partners" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-2xl bg-[#0A0A0A] border border-white/10 overflow-hidden"
          >
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Partner Applications</h2>
              <button
                onClick={() => exportToCSV(partnerApplications, "evolve_partners")}
                disabled={partnerApplications.length === 0}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#00FFD1]/10 text-[#00FFD1] rounded-lg hover:bg-[#00FFD1]/20 transition-colors disabled:opacity-50 text-sm"
                data-testid="export-partners-btn"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>

            {partnerApplications.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                No partner applications yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">#</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Location</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerApplications.map((app, index) => (
                      <tr 
                        key={app.id || index} 
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                        <td className="px-4 py-3 text-sm text-white font-medium">{app.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{app.email}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-[#00FFD1]/10 text-[#00FFD1] capitalize">
                            {app.partner_type?.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">{app.location}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {formatDate(app.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </main>
  );
};

export default AdminPage;
